import { RobotApi } from 'api/RobotApi'
import { StationApi } from 'api/StationApi'
import Loader from 'components/Loader/Loader'
import { useCallback, useEffect, useState } from 'react'
import { GAUSIUM_ENABLED, PROGRESS_TYPES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { IRobotInfo, IStation } from 'utils/interfaces'
import { VENDOR } from 'utils/vendorDisplayName'

import RobotListView from './RobotListView/RobotListView'

const PAGE_SIZE = 20

const RobotList = () => {
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [robots, setRobots] = useState<IRobotInfo[]>([])
  const [stations, setStations] = useState<IStation[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageInput, setPageInput] = useState('1')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [totalCount, setTotalCount] = useState(0)

  const fetchData = useCallback(
    async (isInitial = false) => {
      try {
        if (isInitial) {
          setLoadingProgress(PROGRESS_TYPES.WORK)
          const stations = await StationApi.fetchStations()
          setStations(stations)
        }
        const { results } = await RobotApi.fetchRobots(1, 1000, debouncedSearchQuery)
        const filteredResults = GAUSIUM_ENABLED ? results : results.filter(r => r.vendor !== VENDOR.GAUSIUM)
        setRobots(filteredResults)
        setTotalCount(filteredResults.length)

        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          if (isInitial) {
            setLoadingProgress(PROGRESS_TYPES.IDLE)
          }
          return
        }
        if (isInitial) {
          setLoadingProgress(PROGRESS_TYPES.ERROR)
        }
        console.warn('[RobotList] fetchData error:', error)
      }
    },
    [debouncedSearchQuery]
  )

  const fetchStations = async () => {
    const stationsData = await StationApi.fetchStations()
    setStations(stationsData)
  }

  useEffect(() => {
    fetchData(true)
    const interval = setInterval(() => fetchData(), TIMEOUT_FETCHING_STATUS)
    return () => clearInterval(interval)
  }, [fetchData])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchQuery])

  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput)
      const maxPage = Math.ceil(totalCount / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setCurrentPage(val)
      } else {
        setPageInput(currentPage.toString())
      }
    }
  }

  const sortedRobots = [...robots].sort((a, b) => {
    // 1. Priority: Online robots first
    if (a.networkStatus !== b.networkStatus) {
      return a.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE ? -1 : 1
    }

    // 2. Priority: Diagnostic level (higher is more critical: 3=STALE, 2=ERROR, 1=WARN, 0=OK)
    const diagA = a.systemDiagnostics?.level ?? 0
    const diagB = b.systemDiagnostics?.level ?? 0

    if (diagA !== diagB) {
      return diagB - diagA
    }

    // 3. Priority: Emergency Button (pressed/true first)
    if (a.emergencyButton !== b.emergencyButton) {
      return a.emergencyButton ? -1 : 1
    }

    // 4. Priority: Serial Number
    return a.serialNumber.localeCompare(b.serialNumber)
  })

  const currentRobots = sortedRobots.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  return (
    <RobotListView
      robots={currentRobots}
      stations={stations}
      onRobotsUpdated={() => fetchData()}
      onStationsChanged={fetchStations}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      totalCount={totalCount}
      pageSize={PAGE_SIZE}
      pageInput={pageInput}
      setPageInput={setPageInput}
      handlePageJump={handlePageJump}
      loadingProgress={loadingProgress}
    />
  )
}

export default RobotList
